import { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    FormControlLabel, IconButton, Link, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Description, Edit } from '@mui/icons-material';
import { useNotification } from '../Shared/NotificationProvider';
import {
    addReferenceDocument, DOC_TYPE_OPTIONS, getReferenceDocuments, ReferenceDocumentFields, ReferenceDocumentType,
    removeReferenceDocument, resolveSharePointLink, ResolvedSharePointFile, updateReferenceDocument,
} from '../../services/referenceDocuments.service';

type Props = { refId: number; active: boolean };

const typeLabel = (doc: ReferenceDocumentType) =>
    doc.docType === 'other' ? (doc.otherLabel || 'Other') : (DOC_TYPE_OPTIONS.find((o) => o.value === doc.docType)?.label || doc.docType);

const typeOrder = (docType: string) => {
    const index = DOC_TYPE_OPTIONS.findIndex((o) => o.value === docType);
    return index < 0 ? DOC_TYPE_OPTIONS.length : index;
};

const emptyFields: ReferenceDocumentFields = { docType: 'tender_tor', otherLabel: '', title: '', docDate: '', language: '', isFinal: false, summary: '' };

export default function ReferenceDocumentsTab({ refId, active }: Props) {
    const notify = useNotification();
    const [documents, setDocuments] = useState<ReferenceDocumentType[]>([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<ReferenceDocumentType | null>(null);
    const [link, setLink] = useState('');
    const [fields, setFields] = useState<ReferenceDocumentFields>(emptyFields);
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState('');
    const [toDelete, setToDelete] = useState<ReferenceDocumentType | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            setDocuments(await getReferenceDocuments(refId));
        } catch (error: any) {
            notify(error?.message || 'Error loading documents.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Lazy load when the tab is selected (same as the Experts/Partners tabs).
    useEffect(() => {
        if (active && refId) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, refId]);

    const sorted = useMemo(
        () => [...documents].sort((a, b) =>
            typeOrder(a.docType) - typeOrder(b.docType) ||
            (a.docDate || '9999-12-31').localeCompare(b.docDate || '9999-12-31')),
        [documents],
    );

    const openAdd = () => {
        setEditing(null);
        setLink('');
        setFields(emptyFields);
        setDialogError('');
        setDialogOpen(true);
    };

    const openEdit = (doc: ReferenceDocumentType) => {
        setEditing(doc);
        setLink('');
        setFields({
            docType: doc.docType, otherLabel: doc.otherLabel || '', title: doc.title || '', docDate: doc.docDate || '',
            language: doc.language || '', isFinal: !!doc.isFinal, summary: doc.summary || '',
        });
        setDialogError('');
        setDialogOpen(true);
    };

    const setField = (name: keyof ReferenceDocumentFields, value: any) => setFields((prev) => ({ ...prev, [name]: value }));

    const handleSave = async () => {
        setDialogError('');
        if (!editing && !link.trim()) { setDialogError('Please paste the SharePoint link of the file.'); return; }
        if (fields.docType === 'other' && !fields.otherLabel?.trim()) { setDialogError('Please enter a label for the category "Other".'); return; }

        setSaving(true);
        try {
            const payload: ReferenceDocumentFields = {
                ...fields,
                otherLabel: fields.docType === 'other' ? fields.otherLabel!.trim() : null,
                title: fields.title?.trim() || null,
                docDate: fields.docDate || null,
                language: fields.language?.trim() || null,
                summary: fields.summary?.trim() || null,
            };
            // Resolve the link first: if that fails nothing is saved.
            let file: ResolvedSharePointFile | undefined;
            if (link.trim()) file = await resolveSharePointLink(link);
            if (editing) await updateReferenceDocument(editing.id, payload, file);
            else await addReferenceDocument(refId, file!, payload);
            setDialogOpen(false);
            notify(editing ? 'Document updated.' : 'Document added.', 'success');
            await load();
        } catch (error: any) {
            setDialogError(error?.message || 'Error saving the document.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!toDelete) return;
        try {
            await removeReferenceDocument(toDelete.id);
            notify('Document removed from this reference. The file on SharePoint was not touched.', 'success');
            setToDelete(null);
            await load();
        } catch (error: any) {
            notify(error?.message || 'Error removing the document.', 'error');
        }
    };

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Description />
                    <Typography variant="h6">Documents</Typography>
                </Stack>
                <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add document</Button>
            </Stack>

            {loading ? <CircularProgress size={24} /> : sorted.length === 0 ? (
                <Typography color="textSecondary">No documents linked to this reference yet.</Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Category</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Language</TableCell>
                            <TableCell>Final</TableCell>
                            <TableCell>Summary</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.map((doc) => {
                            const name = doc.title || doc.fileName;
                            const summary = doc.summary && doc.summary.length > 120 ? `${doc.summary.slice(0, 120)}...` : doc.summary;
                            return (
                                <TableRow key={doc.id} hover>
                                    <TableCell>{typeLabel(doc)}</TableCell>
                                    <TableCell>
                                        {doc.webUrl ? <Link href={doc.webUrl} target="_blank" rel="noopener noreferrer">{name}</Link> : name}
                                    </TableCell>
                                    <TableCell>{doc.docDate || ''}</TableCell>
                                    <TableCell>{doc.language || ''}</TableCell>
                                    <TableCell>{doc.isFinal === null ? '' : doc.isFinal ? 'Yes' : 'No'}</TableCell>
                                    <TableCell>
                                        <Tooltip title={doc.summary || ''}><span>{summary}</span></Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" aria-label="Edit document" onClick={() => openEdit(doc)}><Edit fontSize="small" /></IconButton>
                                        <IconButton size="small" aria-label="Delete document" onClick={() => setToDelete(doc)}><Delete fontSize="small" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? 'Edit document' : 'Add document'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {dialogError && <Alert severity="error">{dialogError}</Alert>}
                        <TextField
                            label={editing ? 'SharePoint link (optional: link a different file)' : 'SharePoint link'}
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                            required={!editing}
                            fullWidth
                            helperText={editing ? `Currently linked: ${editing.fileName}` : 'Paste the link of an existing SharePoint file. Nothing is uploaded.'}
                        />
                        <TextField select label="Category" value={fields.docType} onChange={(e) => setField('docType', e.target.value)} fullWidth>
                            {DOC_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </TextField>
                        {fields.docType === 'other' && (
                            <TextField label="Label for Other" required value={fields.otherLabel || ''} onChange={(e) => setField('otherLabel', e.target.value)} inputProps={{ maxLength: 100 }} fullWidth />
                        )}
                        <TextField label="Title (optional, file name is used otherwise)" value={fields.title || ''} onChange={(e) => setField('title', e.target.value)} inputProps={{ maxLength: 500 }} fullWidth />
                        <Stack direction="row" spacing={2}>
                            <TextField label="Date" type="date" value={fields.docDate || ''} onChange={(e) => setField('docDate', e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                            <TextField label="Language" value={fields.language || ''} onChange={(e) => setField('language', e.target.value)} inputProps={{ maxLength: 20 }} fullWidth />
                        </Stack>
                        <FormControlLabel control={<Checkbox checked={!!fields.isFinal} onChange={(e) => setField('isFinal', e.target.checked)} />} label="Final version" />
                        <TextField label="Summary" value={fields.summary || ''} onChange={(e) => setField('summary', e.target.value)} multiline minRows={3} fullWidth />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
                <DialogTitle>Remove document?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        "{toDelete?.title || toDelete?.fileName}" will be removed from this reference's document list.
                        The file itself stays on SharePoint and is not deleted.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setToDelete(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>Remove</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
